import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { apiRequest } from '../lib/utils';

const NotificationContext = createContext(null);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

// VAPID public key for push notifications
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

// Convert base64 to Uint8Array for push subscription
function urlBase64ToUint8Array(base64String) {
  if (!base64String) return null;
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export const NotificationProvider = ({ children }) => {
  const [permission, setPermission] = useState('default');
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [swRegistration, setSwRegistration] = useState(null);
  const [pushSubscription, setPushSubscription] = useState(null);
  const [isSubscribing, setIsSubscribing] = useState(false);

  // Initialize service worker and check permission
  useEffect(() => {
    if ('Notification' in window) {
      setPermission(Notification.permission);
    }
    
    // Load notifications from localStorage
    const saved = localStorage.getItem('lifeos_notifications');
    if (saved) {
      const parsed = JSON.parse(saved);
      setNotifications(parsed);
      setUnreadCount(parsed.filter(n => !n.read).length);
    }

    // Register service worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js')
        .then((registration) => {
          console.log('Service Worker registered:', registration);
          setSwRegistration(registration);
          
          // Check existing subscription
          registration.pushManager.getSubscription()
            .then((subscription) => {
              if (subscription) {
                setPushSubscription(subscription);
              }
            });
        })
        .catch((error) => {
          console.error('Service Worker registration failed:', error);
        });
    }
  }, []);

  // Request notification permission - call this ONLY after value moment
  const requestPermission = async () => {
    if (!('Notification' in window)) {
      toast.error('Tarayıcınız bildirimleri desteklemiyor');
      return false;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      
      if (result === 'granted') {
        toast.success('Bildirimler etkinleştirildi');
        // Auto-subscribe to push after permission granted
        await subscribeToPush();
        return true;
      } else {
        toast.info('Bildirimleri uygulama içinden takip edebilirsiniz');
        return false;
      }
    } catch (err) {
      console.error('Notification permission error:', err);
      return false;
    }
  };

  // Subscribe to push notifications
  const subscribeToPush = async () => {
    if (!swRegistration || !VAPID_PUBLIC_KEY) {
      console.log('Push subscription not available - SW or VAPID key missing');
      return null;
    }

    if (isSubscribing) return pushSubscription;
    setIsSubscribing(true);

    try {
      const applicationServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
      
      const subscription = await swRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey
      });

      setPushSubscription(subscription);
      
      // Send subscription to backend
      await apiRequest('/notifications/subscribe', {
        method: 'POST',
        body: JSON.stringify({
          subscription: subscription.toJSON()
        })
      });

      console.log('Push subscription successful');
      return subscription;
    } catch (error) {
      console.error('Push subscription error:', error);
      return null;
    } finally {
      setIsSubscribing(false);
    }
  };

  // Unsubscribe from push notifications
  const unsubscribeFromPush = async () => {
    if (!pushSubscription) return;

    try {
      await pushSubscription.unsubscribe();
      setPushSubscription(null);

      // Notify backend
      await apiRequest('/notifications/unsubscribe', {
        method: 'POST'
      });

      toast.success('Push bildirimleri kapatıldı');
    } catch (error) {
      console.error('Push unsubscription error:', error);
    }
  };

  // Send local notification (in-app + browser if permitted)
  const sendNotification = useCallback((title, options = {}) => {
    const notification = {
      id: Date.now().toString(),
      title,
      body: options.body || '',
      type: options.type || 'info',
      timestamp: new Date().toISOString(),
      read: false,
      data: options.data || {}
    };

    // Add to in-app notifications
    setNotifications(prev => {
      const updated = [notification, ...prev].slice(0, 50);
      localStorage.setItem('lifeos_notifications', JSON.stringify(updated));
      return updated;
    });
    setUnreadCount(prev => prev + 1);

    // Send browser notification if permitted (for local notifications)
    if (permission === 'granted' && 'Notification' in window && !options.skipBrowser) {
      try {
        new Notification(title, {
          body: options.body,
          icon: '/logo192.png',
          badge: '/logo192.png',
          tag: notification.id,
          requireInteraction: options.requireInteraction || false
        });
      } catch (err) {
        console.error('Browser notification error:', err);
      }
    }

    return notification;
  }, [permission]);

  const markAsRead = (id) => {
    setNotifications(prev => {
      const updated = prev.map(n => 
        n.id === id ? { ...n, read: true } : n
      );
      localStorage.setItem('lifeos_notifications', JSON.stringify(updated));
      return updated;
    });
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllAsRead = () => {
    setNotifications(prev => {
      const updated = prev.map(n => ({ ...n, read: true }));
      localStorage.setItem('lifeos_notifications', JSON.stringify(updated));
      return updated;
    });
    setUnreadCount(0);
  };

  const clearNotifications = () => {
    setNotifications([]);
    setUnreadCount(0);
    localStorage.removeItem('lifeos_notifications');
  };

  const value = {
    permission,
    notifications,
    unreadCount,
    pushSubscription,
    isSubscribing,
    requestPermission,
    subscribeToPush,
    unsubscribeFromPush,
    sendNotification,
    markAsRead,
    markAllAsRead,
    clearNotifications
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
